import { useMemo, useEffect } from "react";
import { debounce } from "lodash";

/** Avoid user to click several times */
export const DEBOUNCE_DELAY_MS = 500;

/**
 * Debounced callback for functions that accept a single argument of type T.
 * Returns the debounced function.
 */
export function useDebouncedCallback<T>(
  callback: (value: T) => void,
  delay: number
): ReturnType<typeof debounce> {
  const debouncedFn = useMemo(() => {
    function wrapped(value: T): void {
      callback(value);
    }

    return debounce(wrapped, delay, {
      /* eslint-disable @typescript-eslint/naming-convention -- Names from debounce method */
      leading: true,
      trailing: false,
      /* eslint-enable */
    });
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}
